import { Close, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Alert,
  Button,
  FormControl,
  IconButton as JoyIconButton,
  Input,
  Option,
  Select,
} from "@mui/joy";
import { Box, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import moment from "moment";
import { KeyboardEvent, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Talent_Entity } from "../../entities/talent";
import { setSelectedTalentID } from "../../redux/filmsReducer";
import { usePositions } from "../../redux/position/positionHooks";
import { currencyFormatter } from "../../constants";
import {
  useDeleteTalentMutation,
  useUpdateTalentMutation,
} from "../../redux/talent/talentEndpoints";
import {
  useDeleteTalentRateChangeMutation,
  useGetTalentRateChangesQuery,
} from "../../redux/talent/talentRateChangeEndpoints";
import { useTalents } from "../../redux/talent/talentHooks";
import { RouterConfig } from "../config/routerConfig";

/** A 4-decimal rate as dollars; no rate on file reads as "—", not $0.00/h. */
function formatRate(rate: number) {
  return rate ? `${currencyFormatter.format(rate / 10000)}/h` : "—";
}

/**
 * TalentForm
 * @params onChange
 * @returns {ReactElement} Talent profile
 */
export default function FormTalent(props: FormTalentProps) {
  const confirm = useConfirm();
  const { talentsMap: talents } = useTalents();
  const navigate = useNavigate();
  const [updateTalent] = useUpdateTalentMutation();
  const [deleteTalent] = useDeleteTalentMutation();
  const [showSsn, setShowSsn] = useState(false);
  const [showEin, setShowEin] = useState(false);
  const sourceTalent = useMemo(
    () => (props.talentID ? talents?.[props.talentID] : props.talent),
    [props.talentID, props.talent, talents]
  );
  const [talent, setTalent] = useState<Partial<Talent_Entity>>(
    sourceTalent ? { ...sourceTalent } : {}
  );

  const { positions } = usePositions();

  const dispatch = useDispatch();
  const positionOptions = (positions ?? []).map((position) => ({
    value: position.id,
    label: position.name,
  }));

  useEffect(() => {
    setTalent((current) => {
      if (!sourceTalent) return {};

      const sourceUpdatedAt = sourceTalent.updatedAt ?? "";
      const currentUpdatedAt = current.updatedAt ?? "";
      if (
        current.id !== sourceTalent.id ||
        currentUpdatedAt !== sourceUpdatedAt
      ) {
        return { ...sourceTalent };
      }

      return current;
    });
  }, [sourceTalent]);

  const handleTextChange =
    (name: keyof Talent_Entity) => (value: string | number) => {
      setTalent((current) => {
        const nextTalent = { ...current, [name]: value };
        if (props.onChange != undefined) {
          props.onChange(nextTalent as Talent_Entity);
        }
        return nextTalent;
      });
    };

  const talentID = talent.id;

  const canSave =
    typeof talentID === "number" && Number.isFinite(talentID) && talentID > 0;

  const handleSave = () => {
    if (!canSave) return;
    if (props.onSave != undefined) {
      props.onSave(talent as Talent_Entity);
    } else {
      updateTalent({ id: talentID, body: talent });
    }
  };

  const handleFieldKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    handleSave();
  };

  // The base rate is stored as a 4-decimal integer but typed as dollars, and
  // converting on every keystroke eats the decimal point as it's typed — so
  // the input owns a string and converts only when committing.
  const [baseRateText, setBaseRateText] = useState("");
  useEffect(() => {
    const rate = talent.baseHourlyRate ?? 0;
    setBaseRateText(rate ? `${rate / 10000}` : "");
  }, [talent.id, talent.baseHourlyRate]);

  const commitBaseRate = () => {
    const parsed = Math.round(Number(baseRateText) * 10000);
    const baseHourlyRate = Number.isFinite(parsed) ? parsed : 0;
    handleTextChange("baseHourlyRate")(baseHourlyRate);
    // Partial body on purpose: handleSave reads `talent` from this render's
    // closure, which doesn't hold the value committed one line up.
    if (canSave) updateTalent({ id: talentID, body: { baseHourlyRate } });
  };

  // Newest first, so the top line answers "when was the latest increase".
  const { data: rateChanges } = useGetTalentRateChangesQuery(talentID ?? 0, {
    skip: !canSave,
  });
  const [deleteRateChange] = useDeleteTalentRateChangeMutation();
  const [showAllRateChanges, setShowAllRateChanges] = useState(false);
  const rateHistory = useMemo(
    () =>
      [...(rateChanges ?? [])].sort((a, b) =>
        (b.createdAt ?? "").localeCompare(a.createdAt ?? "")
      ),
    [rateChanges]
  );
  // The recent moves are the ones people ask about; a long history folds.
  const visibleRateHistory = showAllRateChanges
    ? rateHistory
    : rateHistory.slice(0, 5);

  const getValue = (key: keyof Talent_Entity | "ssn" | "ein") => {
    return (talent?.[key] ?? "") as string | number;
  };

  const inputSx = {
    boxShadow: "none",
    "--Input-focusedHighlight": "transparent",
    "&:hover": {
      boxShadow: "none",
    },
    "&:focus-within": {
      boxShadow: "none",
    },
  };

  function deleteTalentRequest() {
    if (!canSave) return;
    confirm({
      description: `You are about to delete the talent ${talent.firstName} ${talent.lastName}`,
    })
      .then(() => {
        deleteTalent(talentID);
        navigate(`/${RouterConfig.talents}`);
      })
      .catch(() => {
        /* ... */
      });
  }

  return (
    <Grid container sx={style.root} spacing={2}>
      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {props.header != false ? (
          <Typography variant="h5">
            {talent && talent.id
              ? `${talent?.firstName} ${talent?.lastName}`
              : "New Talent"}
          </Typography>
        ) : (
          []
        )}
        <>
          {props.onClose ? (
            <Tooltip title="Close Panel">
              <IconButton
                onClick={() =>
                  props.onClose != undefined
                    ? props.onClose()
                    : dispatch(setSelectedTalentID())
                }
              >
                <Close />
              </IconButton>
            </Tooltip>
          ) : (
            []
          )}
        </>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle1">General</Typography>
      </Grid>
      <Grid item xs={12}>
        {!talent.active ? (
          <Alert
            variant="solid"
            endDecorator={
              <Button
                variant={talent.active ? "soft" : "solid"}
                onClick={() => {
                  updateTalent({
                    id: talent.id,
                    body: {
                      ...talent,
                      active: !talent.active,
                    },
                  });
                }}
              >
                {talent.active ? "Archive" : "Unarchive"}
              </Button>
            }
          >
            {talent.firstName} {talent.lastName} is archived.
          </Alert>
        ) : (
          <Button
            variant={talent.active ? "soft" : "solid"}
            onClick={() => {
              updateTalent({
                id: talent.id,
                body: { ...talent, active: !talent.active },
              });
            }}
          >
            {talent.active ? "Archive" : "Unarchive"} {talent.firstName}{" "}
            {talent.lastName}
          </Button>
        )}
      </Grid>
      <Grid item xs={5}>
        <FormControl>
          <Input
            size="sm"
            autoComplete="off"
            startDecorator="First Name:"
            sx={inputSx}
            value={getValue("firstName")}
            onChange={(e) => handleTextChange("firstName")(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleFieldKeyDown}
          />
        </FormControl>
      </Grid>
      <Grid item xs={5}>
        <FormControl>
          <Input
            size="sm"
            autoComplete="off"
            startDecorator="Last Name:"
            sx={inputSx}
            value={getValue("lastName")}
            onChange={(e) => handleTextChange("lastName")(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleFieldKeyDown}
          />
        </FormControl>
      </Grid>
      <Grid item xs={2}>
        <FormControl>
          <Input
            size="sm"
            autoComplete="off"
            startDecorator="Code:"
            sx={inputSx}
            value={getValue("code")}
            onChange={(e) => handleTextChange("code")(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleFieldKeyDown}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <Select<number>
            slotProps={{
              listbox: { sx: { zIndex: 9999 } },
            }}
            size="sm"
            startDecorator="Position:"
            sx={inputSx}
            value={Number(getValue("positionID")) || null}
            onChange={(_, value) => {
              handleTextChange("positionID")(value ?? 0);
              handleSave();
            }}
          >
            {positionOptions.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <Input
            size="sm"
            type="number"
            autoComplete="off"
            startDecorator="Base Hourly Rate: $"
            endDecorator="/h"
            sx={inputSx}
            value={baseRateText}
            onChange={(e) => setBaseRateText(e.target.value)}
            onBlur={commitBaseRate}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitBaseRate();
            }}
          />
        </FormControl>
      </Grid>
      {rateHistory.length ? (
        <Grid item xs={12}>
          <Box sx={{ px: 1.5 }}>
            {visibleRateHistory.map((change) => (
              <Box
                key={change.id}
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ opacity: 0.8 }}
                >
                  {moment(change.createdAt).format("ll")} —{" "}
                  {formatRate(change.fromRate)} → {formatRate(change.toRate)}
                  {change.author ? ` by ${change.author}` : ""}
                </Typography>
                <IconButton
                  size="small"
                  aria-label="Delete rate change"
                  sx={{ opacity: 0.5, "&:hover": { opacity: 1 } }}
                  onClick={() => {
                    confirm({
                      description: `You are about to delete this rate change from ${talent.firstName} ${talent.lastName}'s history.`,
                    })
                      .then(() => {
                        deleteRateChange(change.id);
                      })
                      .catch(() => {
                        /* ... */
                      });
                  }}
                >
                  <Close sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
            {rateHistory.length > visibleRateHistory.length ? (
              <Button
                size="sm"
                variant="plain"
                onClick={() => setShowAllRateChanges(true)}
              >
                View more ({rateHistory.length - visibleRateHistory.length})
              </Button>
            ) : null}
          </Box>
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <FormControl>
          <Input
            size="sm"
            autoComplete="off"
            startDecorator="Company:"
            sx={inputSx}
            value={getValue("company")}
            onChange={(e) => handleTextChange("company")(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleFieldKeyDown}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <Input
            size="sm"
            type="email"
            autoComplete="off"
            startDecorator="Email:"
            sx={inputSx}
            value={getValue("email")}
            onChange={(e) => handleTextChange("email")(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleFieldKeyDown}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <Input
            size="sm"
            type="tel"
            autoComplete="off"
            startDecorator="Phone:"
            sx={inputSx}
            value={getValue("phone")}
            onChange={(e) => handleTextChange("phone")(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleFieldKeyDown}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <Input
            size="sm"
            autoComplete="off"
            startDecorator="Dropbox:"
            sx={inputSx}
            value={getValue("dropbox")}
            onChange={(e) => handleTextChange("dropbox")(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleFieldKeyDown}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <Input
            size="sm"
            autoComplete="off"
            startDecorator="Google:"
            sx={inputSx}
            value={getValue("google")}
            onChange={(e) => handleTextChange("google")(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleFieldKeyDown}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle1">Personal Information</Typography>
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <Input
            size="sm"
            type="date"
            autoComplete="off"
            startDecorator="Birthdate:"
            sx={inputSx}
            value={
              getValue("birthDate")
                ? moment(getValue("birthDate")).format("YYYY-MM-DD")
                : ""
            }
            onChange={(e) =>
              handleTextChange("birthDate")(
                e.target.value ? moment(e.target.value).utc().format() : ""
              )
            }
            onBlur={handleSave}
            onKeyDown={handleFieldKeyDown}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <Input
            size="sm"
            autoComplete="off"
            startDecorator="Tax Status:"
            sx={inputSx}
            value={getValue("taxStatus")}
            onChange={(e) => handleTextChange("taxStatus")(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleFieldKeyDown}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <Input
            size="sm"
            autoComplete="off"
            startDecorator="Address:"
            sx={inputSx}
            value={getValue("address")}
            onChange={(e) => handleTextChange("address")(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleFieldKeyDown}
          />
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <FormControl>
          <Input
            size="sm"
            autoComplete="off"
            startDecorator="City:"
            sx={inputSx}
            value={getValue("city")}
            onChange={(e) => handleTextChange("city")(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleFieldKeyDown}
          />
        </FormControl>
      </Grid>
      <Grid item xs={3}>
        <FormControl>
          <Input
            size="sm"
            autoComplete="off"
            startDecorator="Zipcode:"
            sx={inputSx}
            value={getValue("zipcode")}
            onChange={(e) => handleTextChange("zipcode")(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleFieldKeyDown}
          />
        </FormControl>
      </Grid>
      <Grid item xs={3}>
        <FormControl>
          <Input
            size="sm"
            autoComplete="off"
            startDecorator="State:"
            sx={inputSx}
            value={getValue("state")}
            onChange={(e) => handleTextChange("state")(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleFieldKeyDown}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle1">Identification</Typography>
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <Input
            size="sm"
            autoComplete="off"
            type={showSsn ? "text" : "password"}
            startDecorator="SSN:"
            sx={inputSx}
            value={getValue("ssn" as keyof Talent_Entity)}
            onChange={(e) => handleTextChange("ssn")(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleFieldKeyDown}
            endDecorator={
              <JoyIconButton
                size="sm"
                variant="plain"
                onClick={() => setShowSsn((s) => !s)}
              >
                {showSsn ? <VisibilityOff /> : <Visibility />}
              </JoyIconButton>
            }
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <Input
            size="sm"
            autoComplete="off"
            type={showEin ? "text" : "password"}
            startDecorator="EIN:"
            sx={inputSx}
            value={getValue("ein" as keyof Talent_Entity)}
            onChange={(e) => handleTextChange("ein")(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleFieldKeyDown}
            endDecorator={
              <JoyIconButton
                size="sm"
                variant="plain"
                onClick={() => setShowEin((s) => !s)}
              >
                {showEin ? <VisibilityOff /> : <Visibility />}
              </JoyIconButton>
            }
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <Input
            size="sm"
            autoComplete="off"
            startDecorator="UUID:"
            sx={inputSx}
            value={getValue("userUUID")}
            onChange={(e) => handleTextChange("userUUID")(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleFieldKeyDown}
          />
        </FormControl>
      </Grid>
      {canSave ? (
        <>
          <Grid item xs={12}>
            <Typography variant="subtitle1">Danger Zone</Typography>
          </Grid>
          <Grid item xs={12}>
            <Button
              size="sm"
              variant="outlined"
              color="error"
              onClick={deleteTalentRequest}
            >
              <i className="fa-duotone fa-trash-can pright"></i> Delete
            </Button>
          </Grid>
        </>
      ) : (
        []
      )}
    </Grid>
  );
}

const style = {
  root: {
    p: 2,
  },
};

export type FormTalentProps = {
  talentID?: number;
  header?: boolean;
  talent?: Talent_Entity;
  onChange?: (talent: Talent_Entity) => void;
  onSave?: (talent: Talent_Entity) => void;
  onClose?: () => void;
};
